import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { useAuthorizationService } from '../api/authorization';
import { useDispatch } from 'react-redux';
import { userAuthzLoaded } from '../redux/rootAction';
import PageHistoryTracker from '../components/page-history-tracker/PageHistoryTracker';
import IdleManagerPopup from '../components/idle-manager';
import { useIdleManager } from '../components/idle-manager/useIdleManager';
import { useUserActivityDetector } from '../components/idle-manager/useUserActivityDetector';
import config from '../config';
import {leaveAppIfPlatformMaintenance} from '../utils/maintenanceLeaveApp';

interface PrivateRouteProps {
  isAuthenticated: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ isAuthenticated }) => {
  const authorizationService = useAuthorizationService();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openIdlePopup, setOpenIdlePopup] = useState(false);

  const idleManagerConfig = useMemo(
    () => ({
      checkInterval: 1 * 90 * 1000,
      maxIdleTime: config.USER_IDLE_TIMEOUT_SEC * 1000,
      gracePeriod: config.USER_IDLE_GRACEPERIOD_SEC * 1000
    }),
    []
  );

  const idleManager = useIdleManager(idleManagerConfig);

  const logOut = useCallback(() => {
    setOpenIdlePopup(false);
    navigate('/logout');
  }, [navigate]);

  const stayLoggedIn = useCallback(() => {
    setOpenIdlePopup(false);
    idleManager.resetTimer();
    idleManager.checkNow();
  }, [idleManager.resetTimer, idleManager.checkNow]);

  // Reset the idle timer when user activity is detected
  const handleUserActivity = useCallback(() => {
    idleManager.resetTimer();
  }, [idleManager.resetTimer]);

  // Activity detection is disabled when the warning dialog is showing
  // to force the user to explicitly choose Stay or Logout
  useUserActivityDetector(
    handleUserActivity,
    1000,
    isAuthenticated && !openIdlePopup
  );

  // Load authorization on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    const loadAuthz = async () => {
      await authorizationService.authorize();
      dispatch(userAuthzLoaded(`${new Date()}`));
    };
    dispatch(userAuthzLoaded(''));
    loadAuthz();
  }, [isAuthenticated]);

  // Start/stop idle manager based on authentication state
  useEffect(() => {
    if (!isAuthenticated) return;
    idleManager.start();
    return () => {
      idleManager.stop();
    };
  }, [isAuthenticated]);

  /* Poll for idle threshold and show warning.
   lastUserActivityRef.current is read inside the callback — not a dep —
   so activity events never cause this effect to re-run.
  */
  useEffect(() => {
    if (!isAuthenticated || !idleManager.isActive) return;

    const { lastUserActivityRef } = idleManager;

    // Check if the user has been idle for too long and show the warning popup
    const checkWarningCondition = () => {
      const idleForSec = Math.floor(
        (Date.now() - lastUserActivityRef.current) / 1000
      );

      if (idleForSec >= config.USER_LAST_ACTIVITY_TIMEFRAME_BEFORE_LOGOUT_SEC) {
        setOpenIdlePopup(true);
      }
    };

    const warningInterval = setInterval(
      checkWarningCondition,
      config.USER_ACTIVITY_INTERVAL_CHECK_MILISEC
    );
    return () => clearInterval(warningInterval);
  }, [isAuthenticated, idleManager.isActive]);

  // Force logout if the session API call fails
  useEffect(() => {
    if (idleManager.isAuthenticated !== false) {
      return;
    }

    const leaveSession = async () => {
      const leftForMaintenance = await leaveAppIfPlatformMaintenance(dispatch);
      if (!leftForMaintenance) {
        navigate('/logout');
      }
    };

    void leaveSession();
  }, [idleManager.isAuthenticated, dispatch, navigate]);

  // All hooks are above — safe to do early return now
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <>
      <PageHistoryTracker />
      <IdleManagerPopup
        isOpen={openIdlePopup}
        initialCountdownSec={config.USER_LOGOUT_POPUP_SEC}
        onStayLoggedIn={stayLoggedIn}
        onLogOut={logOut}
      />
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </>
  );
};

export default PrivateRoute;
